import { useMemo } from "react";
import { motion } from "framer-motion";
import { Shield, Mic, Gift, Radio, MapPin, Sparkles, CheckCircle } from "lucide-react";
import { flags } from "@/config/featureFlags";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import AwardBadge3D from "./AwardBadge3D";

/**
 * ListingHero3D — premium additive layer for the business profile page hero.
 *
 * Design principles:
 *   - **Additive, never destructive**: renders BEHIND and AROUND the existing
 *     hero content. The consumer wraps their hero JSX in <ListingHero3D>.
 *   - Uses existing `background_image_url` when present, otherwise a premium
 *     dark-navy + gold-glow gradient fills in. Zero data migration required.
 *   - No WebGL. Pure CSS transforms + SVG blur + Framer Motion. Bundle-safe.
 *   - Feature-flagged (`ENABLE_3D_LISTING_HERO`) with URL kill-switch.
 *   - Respects `prefers-reduced-motion` (no float, no shimmer, static gradient).
 *   - Mobile fallback: no floating trust chips (they'd crowd the tap targets),
 *     but the depth-gradient stage still renders.
 */
export default function ListingHero3D({ business, children, backgroundImageUrl }) {
  const enabled = flags.ENABLE_3D_LISTING_HERO;
  const { prefersReducedMotion, isMobile } = useMotionPreferences();

  // Trust chips — only render the ones the data supports (no lies about badges)
  const chips = useMemo(() => {
    const out = [];
    if (business?.is_best_of_best || business?.is_ai_certified) {
      out.push({ icon: Sparkles, label: "Verified Best of Best", color: "#F59E0B" });
    }
    if (business?.is_verified) {
      out.push({ icon: Shield, label: "Verified Business", color: "#10B981" });
    }
    if (business?.ai_voice_enabled) {
      out.push({ icon: Mic, label: "AI Voice Active", color: "#00E5FF" });
    }
    if (business?.has_ai_agent || business?.ai_system_prompt) {
      out.push({ icon: CheckCircle, label: "AI Chat Active", color: "#7C3AED" });
    }
    if (business?.coupon_code || business?.has_rewards) {
      out.push({ icon: Gift, label: "Rewards Available", color: "#EC4899" });
    }
    if (business?.livecast_enabled || (business?.livecasts && business.livecasts.length > 0)) {
      out.push({ icon: Radio, label: "LiveCast Ready", color: "#EF4444" });
    }
    if (business?.is_claimed) {
      out.push({ icon: CheckCircle, label: "LocalBest Approved", color: "#10B981" });
    }
    return out;
  }, [business]);

  const marketChip = useMemo(() => {
    const city = business?.city;
    const state = business?.state;
    if (!city && !state) return null;
    return [city, state].filter(Boolean).join(", ");
  }, [business]);

  // If flag off, render children verbatim — total no-op wrapper (safest fallback)
  if (!enabled) return <>{children}</>;

  const bg = backgroundImageUrl || business?.background_image_url || null;
  const hasBg = Boolean(bg);
  const animate = !prefersReducedMotion;

  return (
    <div className="relative" data-testid="listing-hero-3d">
      {/* ---------- Ambient depth stage (behind hero content) ---------- */}
      {/* When a cover/background image exists we render NOTHING over it — no gradient, glow, or noise (user directive). */}
      {!hasBg && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 0 }}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 20% 20%, rgba(124,58,237,0.18), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(0,229,255,0.14), transparent 55%), linear-gradient(180deg, #0B1220 0%, #020817 100%)",
            }}
          />

          {/* Soft gold depth lighting */}
          <motion.div
            className="absolute -top-16 -right-16 w-[420px] h-[420px] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(245,158,11,0.22), transparent 65%)",
              filter: "blur(20px)",
            }}
            animate={animate ? { opacity: [0.55, 0.9, 0.55], scale: [1, 1.06, 1] } : undefined}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Cyan accent depth from bottom-left */}
          <motion.div
            className="absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 65%)",
              filter: "blur(28px)",
            }}
            animate={animate ? { opacity: [0.45, 0.75, 0.45], scale: [1, 1.05, 1] } : undefined}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
          />

          {/* Fine grain noise texture for premium feel (SVG, no extra request) */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.06] mix-blend-overlay"
            xmlns="http://www.w3.org/2000/svg"
          >
            <filter id="lb-hero-noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
              <feColorMatrix values="0 0 0 0 0.55  0 0 0 0 0.55  0 0 0 0 0.55  0 0 0 0.9 0" />
            </filter>
            <rect width="100%" height="100%" filter="url(#lb-hero-noise)" />
          </svg>
        </div>
      )}

      {/* ---------- Actual hero content passed in by parent (untouched) ---------- */}
      <div className="relative" style={{ zIndex: 1 }}>{children}</div>

      {/* ---------- Floating 3D award badge (best_of_best only) ---------- */}
      {(business?.is_best_of_best || business?.is_ai_certified) && (
        <div
          className="absolute pointer-events-none hidden md:block"
          style={{ top: 12, right: 20, zIndex: 2 }}
          data-testid="listing-hero-3d-badge"
        >
          <AwardBadge3D business={business} size="lg" testid="listing-hero-award-badge" />
        </div>
      )}

      {/* ---------- Floating trust chips row (desktop only) ---------- */}
      {!isMobile && chips.length > 0 && (
        <div
          className="absolute left-0 right-0 bottom-2 z-[2] pointer-events-none px-4 sm:px-6 lg:px-8"
          data-testid="listing-hero-3d-chips"
        >
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-end gap-2 pointer-events-auto">
            {chips.slice(0, 6).map((c, idx) => {
              const Icon = c.icon;
              return (
                <motion.span
                  key={c.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + idx * 0.08, duration: 0.35 }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md border shadow-sm"
                  style={{
                    background: "rgba(15, 23, 42, 0.55)",
                    color: c.color,
                    borderColor: `${c.color}55`,
                  }}
                  data-testid={`listing-hero-3d-chip-${idx}`}
                >
                  <Icon className="w-3 h-3" />
                  {c.label}
                </motion.span>
              );
            })}
            {marketChip && (
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.35 }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white/85 backdrop-blur-md bg-black/45 border border-white/10"
                data-testid="listing-hero-3d-market-chip"
              >
                <MapPin className="w-3 h-3" />
                {marketChip}
              </motion.span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
